import { z } from "zod";
import { CONVERSATION_TITLE_MAX_LENGTH } from "../agent/conversation-titles";
import { CONVERSATION_ID_PATTERN } from "../iam/ws-ticket";
/**
 * The person's conversations with the trainer (plan D32, D33) —
 * `GET /me/conversations`, `PUT /me/conversations/:id/seen`, and the two
 * management routes, `DELETE /me/conversations/:id` and
 * `PUT /me/conversations/:id/title`.
 *
 * ── OWN CONVERSATIONS ONLY ──────────────────────────────────────────────────
 * `subject_account IS NULL`: what the person said to the trainer about
 * themselves. A trainer's professional record about a student is never on
 * this list, for either of them (`D75`, `D79`).
 *
 * ── NO MESSAGE CONTENT IN D1 (`D78`) ────────────────────────────────────────
 * `title` is the first user message, truncated, read from the conversation's
 * Durable Object over the agent Worker's `conversationTitles` RPC for the
 * {@link CONVERSATION_LIST_TITLED} most recent entries. It is `null` beyond
 * those, and whenever the object did not answer — a missing title never
 * blocks the list.
 *
 * ── A NEW CONVERSATION HAS NO ENDPOINT ──────────────────────────────────────
 * The client generates a UUID and connects; the socket's `onConnect`
 * registers it. That is also how a DELETED id comes back: the default
 * conversation is the absence of `?conversation=`, so it is always reachable,
 * and connecting to it after a delete registers a fresh row over the deleted
 * one.
 */
/** At most this many entries, most recent first. */
export const CONVERSATION_LIST_MAX = 50;
/** How many of the most recent entries the server asks titles for. Equal to
 *  `CONVERSATION_TITLES_MAX_IDS` in `../agent/conversation-titles`. */
export const CONVERSATION_LIST_TITLED = 20;
// GET /me/conversations      (own conversations only: subject_account IS NULL)
export const conversationListOutput = z.object({
    conversations: z
        .array(z.object({
        conversationId: z.string().regex(CONVERSATION_ID_PATTERN),
        createdAt: z.iso.datetime(),
        lastActivityAt: z.iso.datetime(),
        // first user message, truncated; null beyond the 20 most recent or
        // when the trainer DO did not answer
        title: z.string().min(1).max(80).nullable(),
        unread: z.boolean(),
    }))
        .max(CONVERSATION_LIST_MAX), // most recent first
});
// PUT /me/conversations/:id/seen   body {}   → 204. Stores seen_at = now().
// A new conversation has no endpoint. The client generates a UUID and
// connects, and the socket's onConnect registers it (as it does today).
/** `PUT …/seen` takes an empty object; anything else is refused. */
export const conversationSeenInput = z.strictObject({});
/** The `:id` path parameter, refused with a 400 before any read. */
export const conversationIdParam = z.string().regex(CONVERSATION_ID_PATTERN);
// DELETE /me/conversations/:id   no body → 200 { deleted }.
// The caller's OWN conversation only; anything else is 404
// `conversation.not_found`. The transcript's Durable Object is erased first,
// then the index row is soft-deleted (`D153`'s order). A repeat answers
// `deleted: false`: the row is already gone, and a retry after a timeout is
// not an error.
/** `deleted: false` is the repeat — nothing was live under that id. */
export const conversationDeleteOutput = z.object({ deleted: z.boolean() });
// PUT /me/conversations/:id/title   body { title } → 200 { title }.
// The title is message content, so it is stored in the conversation's Durable
// Object and never in D1 (`D78`, `D170`). A title the person set is never
// replaced by the one derived from the first message, a clear included.
/** The longest title a person may set: the list's own bound. */
export const CONVERSATION_RENAME_MAX_LENGTH = CONVERSATION_TITLE_MAX_LENGTH;
/** Trimmed, then 1..80. Strict, so an unknown key is a 422 (`D172`). */
export const conversationTitleInput = z.strictObject({
    title: z.string().trim().min(1).max(CONVERSATION_RENAME_MAX_LENGTH),
});
/** The title as stored — the trimmed one the list will show. */
export const conversationTitleOutput = z.object({
    title: z.string().min(1).max(CONVERSATION_RENAME_MAX_LENGTH),
});
//# sourceMappingURL=conversation.js.map